import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import client from "@/lib/api";
import Divider from "@/components/Divider";
import FlipThrough from "@/components/FlipThrough";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = `${API_BASE}/api/images/brand/cover`;
const RECIPE_IMG = `${API_BASE}/api/images/brand/tamarind-glazed-sweet-potato-wedges`;
const SPICE_IMG = `${API_BASE}/api/images/brand/millet-berbere-bowl`;
const BOOK_IMG = `${API_BASE}/api/images/brand/chapter-sweet-close`;
const AFRICAN_IMG = `${API_BASE}/api/images/brand/peanut-braised-greens`;

export default function Landing() {
  const [email, setEmail] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const submitSampler = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await client.post("/email/capture", { email, source: "sampler" });
      setDownloadUrl(data.download_url || "/sampler.pdf");
      toast.success("Check your email, your sampler is on the way!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div>
      {/* HERO — full-bleed editorial */}
      <section
        className="relative bg-mizizi-forest text-mizizi-cream overflow-hidden isolate"
        data-testid="hero-section"
      >
        {/* Full-bleed background */}
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover object-center"
          />
          {/* Editorial vignette — dark at top & bottom, softer at center */}
          <div className="absolute inset-0 bg-gradient-to-b from-mizizi-charcoal/85 via-mizizi-charcoal/45 to-mizizi-charcoal/85" />
          <div className="absolute inset-0 bg-mizizi-charcoal/25" />
        </div>

        <div className="relative min-h-[92vh] md:min-h-screen flex flex-col items-center justify-center text-center px-6 sm:px-8 py-24 md:py-32">
          <div className="animate-fade-up w-full max-w-3xl mx-auto">
            <div
              className="overline text-mizizi-gold mb-8 md:mb-10 tracking-[0.4em]"
              data-testid="hero-overline"
            >
              A Contemporary African Cookbook  ·  Volume One
            </div>

            <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-8xl leading-[0.98] tracking-tight">
              Rooted at
              <span className="block italic text-mizizi-gold mt-2 md:mt-4">
                the Table.
              </span>
            </h1>

            {/* Hairline ornament */}
            <div className="flex items-center justify-center gap-4 mt-10 md:mt-14" aria-hidden="true">
              <span className="h-px w-16 bg-mizizi-gold/60" />
              <span className="text-mizizi-gold/70 text-xs">◆</span>
              <span className="h-px w-16 bg-mizizi-gold/60" />
            </div>

            <p className="mt-10 md:mt-12 text-lg md:text-xl leading-[1.7] text-mizizi-cream/85 max-w-2xl mx-auto font-light">
              Thirty plant-based recipes inspired by the flavors of home kitchens,
              roadside markets, and slow Sunday tables across Africa.
            </p>

            <div className="mt-14 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a
                href="#sampler"
                data-testid="hero-sampler"
                className="pill pill-editorial-solid"
              >
                Get the Free Sampler
              </a>
              <Link
                to="/shop"
                data-testid="hero-shop"
                className="pill pill-editorial"
              >
                Buy the Cookbook
              </Link>
            </div>

            <div className="mt-20 md:mt-24 overline text-mizizi-cream/55 tracking-[0.5em]">
              Thirty Recipes  ·  Three Menus  ·  Five Foundations
            </div>
          </div>
        </div>
      </section>

      {/* FLIP-THROUGH SHOWCASE */}
      <section className="bg-mizizi-cream py-20 md:py-32" data-testid="flip-showcase">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-mizizi-terracotta mb-4">A First Look</div>
          <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-tight text-mizizi-charcoal">
            Turn the pages.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-mizizi-charcoal/70 max-w-xl mx-auto">
            A brief flip through the cover, the menus and the plates that anchor the book.
          </p>
          <div className="mt-10 md:mt-14">
            <FlipThrough variant="full" />
          </div>
        </div>
      </section>

      {/* PHILOSOPHY TEASER */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-32 grid md:grid-cols-12 gap-10 md:gap-12 items-center">
        <div className="md:col-span-5 order-2 md:order-1">
          <img src={SPICE_IMG} alt="" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <div className="overline text-mizizi-terracotta mb-4">The Philosophy</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-6xl leading-tight text-mizizi-charcoal">
            Cooking from <em className="not-italic text-mizizi-gold">roots</em>, not trends.
          </h2>
          <p className="mt-6 md:mt-8 text-base sm:text-lg leading-relaxed text-mizizi-charcoal/80">
            Mizizi means <em>roots</em> in Swahili. Rooted across Africa — the book gives plant-based African cooking the craft, beauty and attention too often reserved for fine dining, while staying honest to the everyday table. Deep flavours, considered technique, and a seat set for whoever walks in.
          </p>
          <Link to="/philosophy" data-testid="learn-philosophy" className="mt-6 md:mt-8 inline-block overline text-mizizi-gold border-b border-mizizi-gold/50 pb-1 hover:text-mizizi-terracotta">Read the full philosophy →</Link>
        </div>
      </section>

      <Divider />

      {/* RECIPE PREVIEWS */}
      <section className="bg-mizizi-charcoal text-mizizi-cream py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="overline text-mizizi-gold mb-4">Inside the Book</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-tight">A glimpse of the plates.</h2>
            </div>
            <Link to="/preview" data-testid="see-all-recipes" className="hidden md:inline overline text-mizizi-cream/70 hover:text-mizizi-gold">See all recipes →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: RECIPE_IMG, t: "Tamarind-Glazed Sweet Potato", c: "Starters", slug: "tamarind-glazed-sweet-potato-wedges" },
              { img: AFRICAN_IMG, t: "Peanut-Braised Collard Greens", c: "Vegetables", slug: "peanut-braised-greens" },
              { img: SPICE_IMG, t: "Millet Bowl with Berbere Roots", c: "Grains & Legumes", slug: "millet-berbere-bowl" },
            ].map((r, i) => (
              <Link
                key={r.slug}
                to={`/preview/${r.slug}`}
                data-testid={`recipe-preview-${i}`}
                className="recipe-card group block"
              >
                <div className="overflow-hidden aspect-[4/5] bg-mizizi-forest">
                  <img src={r.img} alt={r.t} className="recipe-cover w-full h-full object-cover" />
                </div>
                <div className="mt-5">
                  <div className="overline text-mizizi-gold">{r.c}</div>
                  <div className="recipe-title font-serif text-2xl mt-1 transition-colors">{r.t}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLER CAPTURE */}
      <section id="sampler" className="bg-mizizi-cream py-16 md:py-32 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-10 text-center">
          <div className="overline text-mizizi-terracotta">Complimentary</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-6xl mt-4 leading-tight text-mizizi-charcoal">A Taste of Mizizi.</h2>
          <p className="mt-5 md:mt-6 text-base sm:text-lg text-mizizi-charcoal/80 leading-relaxed">
            Five signature recipes, delivered as a beautifully typeset digital sampler. A quiet welcome to the table.
          </p>
          <form onSubmit={submitSampler} className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-lg mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="sampler-email-input"
              className="w-full flex-1 px-5 py-4 min-h-[52px] bg-transparent border border-mizizi-gold/50 text-mizizi-charcoal placeholder:text-mizizi-charcoal/40 focus:outline-none focus:border-mizizi-terracotta text-base"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="sampler-submit"
              className="pill pill-primary sm:w-auto disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Sampler"}
            </button>
          </form>
          {downloadUrl && (
            <p className="mt-6 text-sm text-mizizi-charcoal/70" data-testid="sampler-success">
              Your sampler is ready — <span className="text-mizizi-gold">check your inbox</span>. (Email is mocked in this demo.)
            </p>
          )}
        </div>
      </section>

      <Divider />

      {/* FROM THE FIRST TABLE (testimonials placeholder, no fabricated quotes) */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-24 text-center">
        <div className="overline text-mizizi-terracotta">From the First Table</div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-tight text-mizizi-charcoal mt-4">
          Real feedback, coming soon.
        </h2>
        <p className="mt-5 md:mt-6 text-base sm:text-lg text-mizizi-charcoal/75 leading-relaxed">
          Mizizi is currently being tested with early readers and home cooks.
          Real feedback from the first table will appear here soon.
        </p>
        <p className="mt-6 text-sm text-mizizi-charcoal/60 italic">
          Tried a Mizizi recipe? We would love to hear what you made.
        </p>
        <a
          href="mailto:hello@mizizitable.com?subject=Mizizi%20feedback"
          data-testid="share-feedback"
          className="pill pill-outline mt-8 inline-flex"
        >
          Share Your Feedback
        </a>
      </section>

      {/* FROM MY TABLE TO YOURS — founder story */}
      <section className="bg-mizizi-cream border-y border-mizizi-gold/20" data-testid="founder-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          {/* Portrait — Ann, founder of Mizizi */}
          <div className="md:col-span-5">
            <figure
              data-testid="founder-portrait"
              className="relative aspect-[4/5] w-full overflow-hidden bg-mizizi-forest/95 ring-1 ring-mizizi-gold/30"
            >
              <img
                src="/founder-ann.jpg"
                srcSet="/founder-ann-mobile.jpg 720w, /founder-ann.jpg 1200w"
                sizes="(max-width: 767px) 92vw, 40vw"
                alt="Ann, founder of Mizizi"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </figure>
          </div>

          {/* Story */}
          <div className="md:col-span-7">
            <div className="overline text-mizizi-terracotta">The Founder</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-tight text-mizizi-charcoal mt-4">
              From My Table to Yours.
            </h2>
            <div className="mt-6 space-y-5 text-base md:text-lg text-mizizi-charcoal/85 leading-[1.75] font-light">
              <p>
                Mizizi grew from a simple, stubborn love — for African food,
                for the way a table pulls people together, for grains and
                greens and roots that carry more flavour than most kitchens
                give them credit for.
              </p>
              <p>
                I wanted a book that gave familiar African ingredients room
                to feel modern, beautiful, generous, exciting. Not one that
                tries to legitimise African cooking through a Western lens,
                and not one that removes pleasure from food — but a book
                that lets you see
                <em className="text-mizizi-charcoal"> grains, legumes, roots,
                greens, fruits, vegetables and spices</em> a little
                differently.
              </p>
              <p>
                <em className="text-mizizi-gold">Mizizi</em> means <em>roots</em>. It's a nod to the
                things that grow from the earth, and to our own quiet
                connections — to place, to memory, to the people who
                cooked before us.
              </p>
              <p>
                This begins as a cookbook and a table. In time it may become
                dining experiences, gatherings, culinary products, and
                other Mizizi experiences. For now — welcome.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-mizizi-forest text-mizizi-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="overline text-mizizi-gold text-center">Questions</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-center mt-4">The details.</h2>
          <div className="mt-10 md:mt-12 divide-y divide-mizizi-gold/30">
            {[
              { id: "access", q: "How do I access the cookbook after purchase?", a: "You receive a signed download link and a magic-link login to the online reader on any device." },
              { id: "plant-based", q: "Is it fully plant-based?", a: "Yes — every one of the 30 recipes is 100% plant-based. No meat, no dairy, no eggs, no honey. The book is written for plant-based diners, flexitarians and curious omnivores alike — the food is generous, deeply seasoned, and never feels like it's asking you to give something up." },
              { id: "print", q: "Can I print recipes?", a: "The full purchase includes a print-ready PDF alongside the mobile-optimized reader." },
              { id: "refund", q: "Refund policy?", a: "Digital downloads are refundable within 7 days if the file has not been accessed." },
            ].map((f, i) => (
              <details key={f.id} className="py-5 md:py-6 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-center gap-4 font-serif text-lg sm:text-xl md:text-2xl">
                  {f.q}
                  <span className="text-mizizi-gold text-2xl shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-base text-mizizi-cream/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-mizizi-charcoal text-mizizi-cream overflow-hidden">
        <img src={BOOK_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-28 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl leading-tight">Come and eat.</h2>
          <p className="mt-5 md:mt-6 text-base sm:text-lg text-mizizi-cream/70 max-w-xl mx-auto">The full digital cookbook, three complete menus, five foundational sauces, and a reader made for phone-in-hand cooking.</p>
          <Link to="/shop" data-testid="cta-shop" className="pill pill-primary mt-8 md:mt-10 inline-flex">Explore the Editions</Link>
        </div>
      </section>
    </div>
  );
}
